import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import * as api from '../api';
import { ShipViewer, DroneViewer } from './three/FleetModels';

const SHIP_ITEMS = [
  { key: 'ship_mining_item', label: 'Mining Ship' },
  { key: 'ship_freighter_item', label: 'Freighter' },
  { key: 'ship_capital_freighter_item', label: 'Capital Freighter' },
  { key: 'ship_cruiser_item', label: 'Cruiser' },
  { key: 'ship_battleship_item', label: 'Battleship' },
];

function FleetPanel() {
  const { ships, drones, refreshFleet, updateInventory } = useGame();
  const [costs, setCosts] = useState({});
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('info');
  const [expandedShip, setExpandedShip] = useState(null);
  const [expandedDrone, setExpandedDrone] = useState(null);
  const [buildCosts, setBuildCosts] = useState(null);
  const [commissionItem, setCommissionItem] = useState('');
  const [commissionName, setCommissionName] = useState('');
  const [commissionSector, setCommissionSector] = useState('');
  const [sectors, setSectors] = useState([]);

  const loadCosts = useCallback(async () => {
    const costMap = {};
    for (const ship of ships) {
      try {
        const { data } = await api.getUpgradeCosts('ship', ship.id);
        costMap[`ship-${ship.id}`] = data;
      } catch { /* ignore */ }
    }
    for (const drone of drones) {
      try {
        const { data } = await api.getUpgradeCosts('drone', drone.id);
        costMap[`drone-${drone.id}`] = data;
      } catch { /* ignore */ }
    }
    setCosts(costMap);
  }, [ships, drones]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  useEffect(() => {
    api.getBuildCosts().then(({ data }) => setBuildCosts(data)).catch(() => {});
    api.getSectors().then(({ data }) => setSectors(data.sectors || [])).catch(() => {});
  }, []);

  // Load fleet limits on mount
  useEffect(() => { refreshFleet(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(null), 4000);
  };

  const handleUpgradeShip = async (shipId, module) => {
    try {
      const { data } = await api.upgradeShip(shipId, module);
      if (data.inventory) updateInventory(data.inventory);
      await refreshFleet();
      showMsg(`Ship ${module} upgraded!`, 'success');
      await loadCosts();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Upgrade failed', 'error');
    }
  };

  const handleUpgradeDrone = async (droneId, stat) => {
    try {
      const { data } = await api.upgradeDrone(droneId, stat);
      if (data.inventory) updateInventory(data.inventory);
      await refreshFleet();
      showMsg(`Drone ${stat} upgraded!`, 'success');
      await loadCosts();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Upgrade failed', 'error');
    }
  };

  const handleBuildShip = async (type) => {
    try {
      const { data } = await api.buildShip(type);
      if (data.inventory) updateInventory(data.inventory);
      await refreshFleet();
      showMsg(`New ${type} ship built!`, 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Build failed', 'error');
    }
  };

  const handleBuildDrone = async (type) => {
    try {
      const { data } = await api.buildDrone(type);
      if (data.inventory) updateInventory(data.inventory);
      await refreshFleet();
      showMsg(`New ${type} drone built!`, 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Build failed', 'error');
    }
  };

  const handleAssignDrone = async (droneId, shipId) => {
    try {
      await api.assignDrone(droneId, shipId || null);
      await refreshFleet();
      showMsg(shipId ? 'Drone assigned to ship' : 'Drone unassigned', 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Assignment failed', 'error');
    }
  };

  const handleCommissionShip = async () => {
    if (!commissionItem || !commissionSector || !commissionName) return;
    try {
      await api.commissionShip(commissionItem, Number(commissionSector), commissionName);
      await refreshFleet();
      showMsg('Ship commissioned from inventory!', 'success');
      setCommissionItem(''); setCommissionName(''); setCommissionSector('');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Commission failed', 'error');
    }
  };

  const handleRepairShip = async (shipId) => {
    try {
      await api.repairShip(shipId);
      await refreshFleet();
      showMsg('Ship repaired!', 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Repair failed', 'error');
    }
  };

  const abbrev = (n) => {
    if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e4) return `${(n / 1e3).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const fmtCost = (cost) => {
    if (!cost) return '—';
    if (typeof cost === 'number') return abbrev(cost);
    if (typeof cost === 'object') {
      const parts = Object.entries(cost).filter(([, v]) => v > 0).map(([k, v]) => `${abbrev(v)} ${k.replace(/_/g, ' ')}`);
      return parts.length > 0 ? parts.join(' · ') : '—';
    }
    return '—';
  };

  return (
    <div className="panel fleet-panel">
      {msg && <div className={`hud-toast hud-toast-${msgType}`}>{msg}</div>}



      {/* Ships */}
      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">▲</span> SHIPS
          <span className="badge">{ships.length}</span>
        </h3>
        <div className="hud-grid fleet-grid">
          {ships.map(s => {
            const c = costs[`ship-${s.id}`] || {};
            const isExpanded = expandedShip === s.id;
            return (
              <div key={s.id} className={`hud-card fleet-card ${isExpanded ? 'expanded' : ''}`} onClick={() => setExpandedShip(isExpanded ? null : s.id)}>
                <div className="fleet-card-top">
                  <ShipViewer ship={s} />
                  <div className="fleet-info">
                    <div className="card-name">{s.name}</div>
                    <div className="card-meta">
                      <span className="level-badge">LV {s.level}</span>
                      <span className="type-tag">{s.type}</span>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="upgrade-panel" onClick={e => e.stopPropagation()}>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">CARGO</span>
                        <span className="stat-value">{s.cargo_capacity}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeShip(s.id, 'cargo')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.cargo)}
                      </button>
                    </div>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">SPEED</span>
                        <span className="stat-value">{s.speed}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeShip(s.id, 'speed')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.speed)}
                      </button>
                    </div>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">SHIELD</span>
                        <span className="stat-value">{s.shield}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeShip(s.id, 'shield')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.shield)}
                      </button>
                    </div>

                    {/* Assigned Drones */}
                    <div className="ship-drones-section">
                      <span className="stat-label">ASSIGNED DRONES</span>
                      <div className="assigned-drones-list">
                        {drones.filter(d => d.ship_id === s.id).map(d => (
                          <div key={d.id} className="assigned-drone-tag">
                            <span>{d.name} (LV {d.level})</span>
                            <button className="btn-unassign" onClick={() => handleAssignDrone(d.id, null)}>✕</button>
                          </div>
                        ))}
                        {drones.filter(d => d.ship_id === s.id).length === 0 && (
                          <span className="hud-empty-sm">No drones assigned</span>
                        )}
                      </div>
                    </div>

                    {/* Repair */}
                    {s.hp < s.max_hp && (
                      <div className="upgrade-row">
                        <div className="upgrade-stat">
                          <span className="stat-label">HP</span>
                          <span className="stat-value">{s.hp}/{s.max_hp}</span>
                        </div>
                        <button className="btn btn-repair" onClick={() => handleRepairShip(s.id)}>
                          🔧 Repair ({(s.max_hp - s.hp) * 2}cr)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Drones */}
      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">⬡</span> DRONES
          <span className="badge">{drones.length}</span>
        </h3>
        <div className="hud-grid fleet-grid">
          {drones.map(d => {
            const c = costs[`drone-${d.id}`] || {};
            const isExpanded = expandedDrone === d.id;
            return (
              <div key={d.id} className={`hud-card fleet-card ${isExpanded ? 'expanded' : ''}`} onClick={() => setExpandedDrone(isExpanded ? null : d.id)}>
                <div className="fleet-card-top">
                  <DroneViewer drone={d} />
                  <div className="fleet-info">
                    <div className="card-name">{d.name}</div>
                    <div className="card-meta">
                      <span className="level-badge">LV {d.level}</span>
                      <span className="type-tag">{d.type}</span>
                      {d.ship_id && (
                        <span className="assigned-tag">⚓ {ships.find(s => s.id === d.ship_id)?.name || 'Assigned'}</span>
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="upgrade-panel" onClick={e => e.stopPropagation()}>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">SPEED</span>
                        <span className="stat-value">{d.speed}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeDrone(d.id, 'speed')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.speed)}
                      </button>
                    </div>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">YIELD</span>
                        <span className="stat-value">{d.yield}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeDrone(d.id, 'yield')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.yield)}
                      </button>
                    </div>
                    <div className="upgrade-row">
                      <div className="upgrade-stat">
                        <span className="stat-label">DURABILITY</span>
                        <span className="stat-value">{d.durability}</span>
                      </div>
                      <button className="btn btn-upgrade" onClick={() => handleUpgradeDrone(d.id, 'durability')}>
                        <span className="upgrade-arrow">↑</span> {fmtCost(c.durability)}
                      </button>
                    </div>

                    {/* Assign to ship */}
                    <div className="drone-assign-row">
                      <span className="stat-label">ASSIGN TO SHIP</span>
                      <select
                        className="hud-select hud-select-sm"
                        value={d.ship_id || ''}
                        onChange={e => handleAssignDrone(d.id, e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">— Unassigned —</option>
                        {ships.map(s => (
                          <option key={s.id} value={s.id}>{s.name} (LV {s.level})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Build New Ships */}
      {buildCosts && (
        <section className="section">
          <h3 className="section-title">
            <span className="title-accent">+</span> BUILD SHIP
          </h3>
          <div className="hud-grid fleet-grid">
            {Object.entries(buildCosts.ships).map(([type, cost]) => {
              const stats = buildCosts.shipStats[type];
              return (
                <div key={type} className="hud-card build-card">
                  <div className="card-name">{type.charAt(0).toUpperCase() + type.slice(1)} Ship</div>
                  <div className="build-stats">
                    <span className="stat-sm">Cargo {stats.cargo_capacity}</span>
                    <span className="stat-sm">Spd {stats.speed}</span>
                    <span className="stat-sm">Shd {stats.shield}</span>
                  </div>
                  <div className="build-cost">
                    {Object.entries(cost).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="cost-item">{v.toLocaleString()} {k.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                  <button className="btn btn-build" onClick={() => handleBuildShip(type)}>BUILD</button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Build New Drones */}
      {buildCosts && (
        <section className="section">
          <h3 className="section-title">
            <span className="title-accent">+</span> BUILD DRONE
          </h3>
          <div className="hud-grid fleet-grid">
            {Object.entries(buildCosts.drones).map(([type, cost]) => {
              const stats = buildCosts.droneStats[type];
              return (
                <div key={type} className="hud-card build-card">
                  <div className="card-name">{type.charAt(0).toUpperCase() + type.slice(1)} Drone</div>
                  <div className="build-stats">
                    <span className="stat-sm">Spd {stats.speed}</span>
                    <span className="stat-sm">Yld {stats.yield}</span>
                    <span className="stat-sm">Dur {stats.durability}</span>
                  </div>
                  <div className="build-cost">
                    {Object.entries(cost).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="cost-item">{v.toLocaleString()} {k.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                  <button className="btn btn-build" onClick={() => handleBuildDrone(type)}>BUILD</button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Commission Ship from Inventory */}
      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">🚀</span> COMMISSION SHIP
        </h3>
        <p className="hud-empty-sm">Convert a ship item from your inventory into an active ship.</p>
        <div className="commission-form">
          <select className="hud-select" value={commissionItem} onChange={e => setCommissionItem(e.target.value)}>
            <option value="">Ship item...</option>
            {SHIP_ITEMS.map(si => <option key={si.key} value={si.key}>{si.label}</option>)}
          </select>
          <select className="hud-select" value={commissionSector} onChange={e => setCommissionSector(e.target.value)}>
            <option value="">Sector...</option>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="text" className="hud-input" placeholder="Ship name..." value={commissionName}
            onChange={e => setCommissionName(e.target.value)} maxLength={30} />
          <button className="btn btn-build" onClick={handleCommissionShip} disabled={!commissionItem || !commissionSector || !commissionName}>
            COMMISSION
          </button>
        </div>
      </section>
    </div>
  );
}

export default FleetPanel;
